import curry from 'lodash/fp/curry'
import curryRight from 'lodash/fp/curryRight'
import compose from 'lodash/fp/compose'
import add from 'lodash/fp/add'
import { getOrNull, getOrEmptyObject } from './selectors'
import { withProp, switchProp, theme } from 'styled-tools'
import hexToRgba from 'hex-to-rgba'

/**
 * remove unit string for value returning number
 *
 * @sig String -> Number
 * @param {String} value - string value
 * @return {Number} stripped value
 */
export const stripUnit = value => parseFloat(value)

/**
 * converts unit size in relative size
 *
 * @param {Number|String} size - size to convert
 * @param {Number|String} base - base size to calculate
 * @return {Number} converted relative value
 */
export const unitToRelative = (size, base) => stripUnit(size) / stripUnit(base)

/**
 * converts unit size in em
 *
 * @sig Number|String -> Number|String -> String
 * @param {Number|String} size - unit size to converts in em
 * @param {Number|String} base - unit base size to calculate em
 * @return {String} value converted to em
 */
export const calcEm = curry((size, base) => `${unitToRelative(size, base)}em`)

/**
 * converts unit size in rem
 *
 * @sig Number|String -> Number|String -> String
 * @param {Number|String} size - unit size to converts in rem
 * @param {Number|String} base - unit base size to calculate rem
 * @return {String} value converted to rem
 */
export const calcRem = curry((size, base) => `${unitToRelative(size, base)}rem`)

/**
 * get the size value in %
 *
 * @sig Number|String -> Number|String -> String
 * @param {Number|String} size - unit to calc in per
 * @param {Number|String} base - base size to calculate per
 * @return {String} value converted to per
 */
export const per = curry((size, base) => `${unitToRelative(size, base) * 100}%`)

/**
 * get the size value in em
 *
 * @sig (Number|String, String) -> Props -> String
 * @param {Number|String} size - to convert to em
 * @param {String} [baseProp='theme.baseSize'] - theme property path to get base size
 * @return {Function} to calculate size in em from props
 */
export const em = (size, baseProp='theme.baseSize') =>
  withProp(baseProp, calcEm(size))

/**
 * get the size value in rem
 *
 * @sig (Number|String, String) -> Props -> String
 * @param {Number|String} size - to convert to rem
 * @return {Function} to calculate size in rem from props
 */
export const rem = size => withProp('theme.baseSize', calcRem(size))

/**
 * add unit to number
 * (curried right)
 * @sig String -> Number|String -> String
 * @param {Number|String} size - size to add unit
 * @param {String} unit - unit symbol
 * @return {String} value with unit
 */
export const withUnit = curryRight((size, unit) => `${size}${unit}`)

/**
 * theme color function
 *
 * @sig String -> Props -> String
 * @param {String} colorName - theme color name
 * @return {Function} theme function for color
 */
export const color = colorName => theme(`colors.${colorName}`)

/**
 * converts color to rgba with amount of opacity
 *
 * @sig (String, Number) -> Props -> String
 * @param {String} colorName - theme color name
 * @param {Number} amount - opacity amount
 * @return {RGBA} rgba color
 */
export const rgba = (colorName, amount = 0.5) => props => hexToRgba(
  color(colorName)(props),
  amount
)

/**
 * creates a dictionary of colors to get theme color prop
 *
 * @param {Array<String>} colors - colors values to switch
 * @return {Object<String,Function>} colors switch dict
 */
export const colorsToSwitch = colors => colors.reduce((values, colorName) => ({
  ...values,
  [colorName]: color(colorName)
}), {})

/**
 * create media query for passed theme media
 *
 * @param {String} media - theme media key
 * @return {CssList} css to apply to media
 */
export const media = media => (...args) => props => props.theme.media[media](...args)

export const mediaMaxWidth = media => withProp(`theme.mediaSizes.${media}`, compose(
  withUnit('px'),
  add(-1)
))

/** @typedef {String} TargetName - media query target name */
/** @typedef {any} PropValue - value of a property commonly used to switchProp */
/** @typedef {any} Value - css value */
/** @typedef {Object.<TargetName,Value>} TargetSwitch - object with target values by target name */
/** @typedef {Object.<PropValue, TargetSwitch>} PropertyTargetsSwitch - object with target switchs by prop values */
/** @typedef {Object.<PropValue, Value>} PropSwitch - object with css values by prop value key */

/**
 * reduce object of props values with targets to
 * prop value with target value
 *
 * @param {PropertyTargetsSwitch} switchs
 * @param {TargetName} target
 * @return {PropSwitch}
 *
 * @example
 * targetsSwitchReducer({
 *  small: {
 *    tablet: '22px',
 *    large: '40px'
 *  },
 *  big: {
 *    tablet: '30px',
 *    large: '50px'
 *  }
 * }, 'tablet')
 * // returns { small: '22px', big: '30px' }
 */
export const targetsSwitchReducer = (propTargetSwitchs, target) => Object.entries(propTargetSwitchs)
  .reduce(
    (acc, [value, targetSwitch]) => ({
      ...acc,
      [value]: getOrEmptyObject(target)(targetSwitch)
    }),
  {})

/**
 * creates a function to get propSwitch for media query target
 *
 * @param {String} prop - prop name to switch value
 * @param {PropertyTargetsSwitch} targetSwitchs - property values with targets switch
 * @param {TargetSwitch} defaultValues - default values if prop dont have value
 * @return {Function} function that accepts props and return value by switchProp
 * @see {@link https://github.com/diegohaz/styled-tools#switchprop}
 */
export const switchPropForTarget = (prop, propTargetSwitchs, defaultValues) => target => switchProp(
  prop,
  targetsSwitchReducer(propTargetSwitchs, target),
  getOrNull(target)(defaultValues)
)
